import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  Timestamp,
  doc,
  getDoc
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { AuditLog } from '../types';
import { getBoxTemplate } from '../constants/industryTemplates';

export class AuditLogService {
  // 監査ログを取得
  static async getAuditLogs(
    companyId: string, 
    filter?: string, 
    selectedDate?: Date,
    limitCount: number = 100
  ): Promise<AuditLog[]> {
    try {
      let q = query(
        collection(db, 'audit_logs'),
        where('companyId', '==', companyId)
      );

      // アクションフィルター（ボイス/サンクスの場合は後でクライアント側でフィルタリング）
      if (filter && filter !== 'all') {
        // ボイス/サンクス以外はサーバー側でフィルタリング
        if (!['voice', 'thanks'].includes(filter)) {
          q = query(q, where('action', '==', filter));
        }
      }

      // 日付フィルター
      if (selectedDate) {
        const startDate = new Date(selectedDate);
        startDate.setHours(0, 0, 0, 0);
        const endDate = new Date(selectedDate);
        endDate.setHours(23, 59, 59, 999);

        q = query(
          q,
          where('timestamp', '>=', Timestamp.fromDate(startDate)),
          where('timestamp', '<=', Timestamp.fromDate(endDate)),
          orderBy('timestamp', 'desc'),
          limit(limitCount)
        );
      } else {
        q = query(q, orderBy('timestamp', 'desc'), limit(limitCount));
      }

      const snapshot = await getDocs(q);
      const auditLogs: AuditLog[] = [];

      for (const docSnapshot of snapshot.docs) {
        const data = docSnapshot.data();
        const auditLog: AuditLog = {
          id: docSnapshot.id,
          timestamp: data.timestamp?.toDate() || new Date(),
          action: data.action || '',
          description: data.description || '',
          userId: data.userId || '',
          userName: data.userName || '不明',
          ipAddress: data.ipAddress || '',
          details: data.details || {},
          companyId: data.companyId || companyId,
        };
        auditLogs.push(auditLog);
      }

      // ボイス/サンクスの場合はクライアント側でフィルタリング
      if (filter && ['voice', 'thanks'].includes(filter)) {
        return auditLogs.filter(log => {
          // memo_added の場合は postType で判定
          if (log.action === 'memo_added' && (log.details as any)?.postType === filter) {
            return true;
          }
          // ステータス変更の場合は postType で判定
          if (log.action === 'status_change' && (log.details as any)?.postType === filter) {
            return true;
          }
          // 既存のログでpostTypeがない場合は、actionから推測
          if (!(log.details as any)?.postType) {
            // actionが'voice'の場合はボイスボックス
            if (log.action === 'voice' && filter === 'voice') {
              return true;
            }
            // actionが'thanks'の場合はサンクスボックス
            if (log.action === 'thanks' && filter === 'thanks') {
              return true;
            }
          }
          // その他のアクションで postType が一致する場合
          if ((log.details as any)?.postType === filter) {
            return true;
          }
          return false;
        });
      }

      return auditLogs;
    } catch (error) {
      console.error('監査ログ取得エラー:', error);
      throw new Error('監査ログの取得に失敗しました。しばらく時間をおいてから再度お試しください');
    }
  }

  // ユーザー名を取得
  static async getUserName(userId: string): Promise<string> {
    try {
      if (!userId) return '不明';
      
      const userDoc = await getDoc(doc(db, 'users', userId));
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const displayName = userData.displayName;
        const email = userData.email;
        
        if (displayName && displayName.trim() !== '') {
          return displayName;
        } else if (email && email.trim() !== '') {
          return email.split('@')[0];
        }
      }
      
      return '不明';
    } catch (error) {
      console.error('ユーザー名取得エラー:', error);
      return '不明';
    }
  }

  // 投稿詳細を取得
  static async getPostDetails(postId: string): Promise<any> {
    try {
      if (!postId) return null;
      
      const postDoc = await getDoc(doc(db, 'posts', postId));
      
      if (postDoc.exists()) {
        return postDoc.data();
      }
      
      return null;
    } catch (error) {
      console.error('投稿詳細取得エラー:', error);
      return null;
    }
  }

  // アクションラベルを取得
  static getActionLabel(action: string, details?: any, industryType?: string): string {
    // メモ投稿の場合は投稿タイプに基づいてラベルを決定
    if (action === 'memo_added' && details?.postType) {
      const boxType = details.postType as 'voice' | 'thanks' | 'theme';
      if (['voice', 'thanks', 'theme'].includes(boxType)) {
        return getBoxTemplate(industryType, boxType).name;
      }
      return '投稿';
    }

    switch (action) {
      case 'login':
        return 'ログイン';
      case 'logout':
        return 'ログアウト';
      case 'post':
        return '投稿';
      case 'admin':
        return '管理操作';
      case 'system':
        return 'システム';
      case 'voice':
        return getBoxTemplate(industryType, 'voice').name;
      case 'thanks':
        return getBoxTemplate(industryType, 'thanks').name;
      case 'theme':
        return getBoxTemplate(industryType, 'theme').name;
      case 'memo_added':
        return 'メモ投稿';
      default:
        return action;
    }
  }

  // アクション色を取得
  static getActionColor(action: string, details?: any): string {
    // メモ投稿の場合は投稿タイプに基づいて色を決定
    if (action === 'memo_added' && details?.postType) {
      switch (details.postType) {
        case 'voice':
          return '#A3D6CC'; // ボイスボックス色（ミントグリーン）
        case 'thanks':
          return '#FFB6C1'; // サンクスボックス色（薄いピンク）
        case 'theme':
          return '#87CEFA'; // テーマボックス色（ライトスカイブルー）
        default:
          return '#4A90E2'; // デフォルト青
      }
    }

    switch (action) {
      case 'login':
        return '#4CAF50'; // 緑
      case 'logout':
        return '#FF9800'; // オレンジ
      case 'post':
        return '#2196F3'; // 青
      case 'admin':
        return '#9C27B0'; // 紫
      case 'system':
        return '#9E9E9E'; // グレー
      case 'voice':
        return '#A3D6CC'; // ボイスボックス色（ミントグリーン）
      case 'thanks':
        return '#FFB6C1'; // サンクスボックス色（薄いピンク）
      case 'theme':
        return '#87CEFA'; // テーマボックス色（ライトスカイブルー）
      case 'memo_added':
        return '#4A90E2'; // 青
      default:
        return '#2196F3'; // デフォルト青
    }
  }

  // ステータス背景色を取得
  static getStatusBackgroundColor(status: string): string {
    switch (status) {
      case '受理':
        return '#87CEEB'; // スカイブルー
      case '対応中':
        return '#FFB366'; // ライトオレンジ
      case '完了':
        return '#90EE90'; // ライトグリーン
      case '表示中':
        return '#90EE90'; // ライトグリーン
      case '非表示':
        return '#FFB6C1'; // ライトピンク
      default:
        return '#D3D3D3'; // グレー
    }
  }

  // ステータステキスト色を取得
  static getStatusTextColor(status: string): string {
    switch (status) {
      case '受理':
        return '#4682B4'; // スチールブルー
      case '対応中':
        return '#D2691E'; // チョコレート
      case '完了':
        return '#228B22'; // フォレストグリーン
      case '表示中':
        return '#228B22'; // フォレストグリーン
      case '非表示':
        return '#CD5C5C'; // インディアンレッド
      default:
        return '#696969'; // グレー
    }
  }

  // タイムスタンプをフォーマット
  static formatTimestampSmart(dateTime: Date): string {
    const now = new Date();
    const diff = now.getTime() - dateTime.getTime();
    
    // 24時間以内は相対表示
    if (diff < 24 * 60 * 60 * 1000) {
      return this.formatTimeAgo(dateTime);
    }
    
    // それ以外は絶対表示
    const year = dateTime.getFullYear().toString().padStart(4, '0');
    const month = (dateTime.getMonth() + 1).toString().padStart(2, '0');
    const day = dateTime.getDate().toString().padStart(2, '0');
    const hours = dateTime.getHours().toString().padStart(2, '0');
    const minutes = dateTime.getMinutes().toString().padStart(2, '0');
    
    return `${year}/${month}/${day} ${hours}:${minutes}`;
  }

  // 相対時間をフォーマット
  static formatTimeAgo(dateTime: Date): string {
    const now = new Date();
    const diff = now.getTime() - dateTime.getTime();
    
    const days = Math.floor(diff / (24 * 60 * 60 * 1000));
    const hours = Math.floor(diff / (60 * 60 * 1000));
    const minutes = Math.floor(diff / (60 * 1000));
    
    if (days > 0) {
      return `${days}日前`;
    } else if (hours > 0) {
      return `${hours}時間前`;
    } else if (minutes > 0) {
      return `${minutes}分前`;
    } else {
      return 'たった今';
    }
  }
}
